/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useContext, useCallback, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import type { MutationUpdaterFn } from 'apollo-client';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useCommentsData,
	useCommentsDataActions,
	CommentActionType,
	CommentType,
	type CommentData,
	type ReplyData,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import { selectUpdateFunction } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext';
import { EditComment } from '@confluence/inline-comments-common/entry-points/EditComment';
import { CommentBody } from '@confluence/inline-comments-common/entry-points/CommentBody';
import {
	handleResolveSuccess,
	handleMutationFailure,
	parseError,
	getTranslatedError,
	isAlreadyDeletedError,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { usePageContentId } from '@confluence/page-context';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CommentDeletionLocation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation';
import { InlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.graphql';
import { buildConsistentActiveCommentsQueryVariables } from '@confluence/comments-panel-queries/entry-points/buildConsistentQueryVariablesHelper';
import {
	type ActiveCommentsQueryType,
	type CommentFooterLocation,
	CommentsType,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import { ActiveCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.graphql';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments';
import type { InlineCommentAuthorKnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery';
import type {
	TopLevelComment,
	GeneralCommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import type { GraphQLContentStatus } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import {
	DELETE_PAGE_COMMENT_EXPERIENCE,
	RESOLVE_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n';
import { i18n as inlineCommentsI18n } from '@confluence/inline-comments-common/entry-points/i18n';
import {
	updateApolloCacheParentCallback as updateApolloCacheGeneralParentCallback,
	updateApolloCacheReplyCallback as updateApolloCacheGeneralReplyCallback,
} from '@confluence/page-comments-queries/entry-points/pageCommentUtils';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getLogger } from '@confluence/logger';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics';
import { fg } from '@confluence/feature-gating';

import type {
	ObjectCommentDeleteGeneralCommentMutation as ObjectCommentDeleteGeneralCommentMutation$data,
	ObjectCommentDeleteGeneralCommentMutationVariables as ObjectCommentDeleteGeneralCommentMutation$variables,
} from './__types__/ObjectCommentDeleteGeneralCommentMutation';
import type {
	ObjectCommentReopenCommentMutation as ObjectCommentReopenCommentMutation$data,
	ObjectCommentReopenCommentMutationVariables as ObjectCommentReopenCommentMutation$variables,
} from './__types__/ObjectCommentReopenCommentMutation';
import type {
	ObjectCommentResolveCommentMutation as ObjectCommentResolveCommentMutation$data,
	ObjectCommentResolveCommentMutationVariables as ObjectCommentResolveCommentMutation$variables,
} from './__types__/ObjectCommentResolveCommentMutation';

const ObjectCommentResolveCommentMutation = gql`
	mutation ObjectCommentResolveCommentMutation($commentIds: [ID]!, $cloudId: ID!) {
		confluence_resolveComments(commentIds: $commentIds, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				resolveProperties {
					resolvedTime
					resolvedUser
					resolvedFriendlyDate
				}
				status
			}
			errors {
				message
				extensions {
					... on ResolveCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const ObjectCommentReopenCommentMutation = gql`
	mutation ObjectCommentReopenCommentMutation($commentId: ID!, $cloudId: ID!) {
		confluence_reopenComment(commentId: $commentId, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				status
			}
			errors {
				message
				extensions {
					... on ReopenCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const ObjectCommentDeleteGeneralCommentMutation = gql`
	mutation ObjectCommentDeleteGeneralCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
type CommentProps = {
	comment: CommentData | ReplyData;
	supportedTopLevelActions: CommentAction[];
	pageMode: PageMode;
	pageInfo: PageInfoNode | null;
	isUnread?: boolean;
	isHovered?: boolean;
	threadKey: string;
	flags?: FlagsStateContainer;
	adjustForNestedReplies?: boolean;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isThreadHovered?: boolean;
	isParentCommentOpen?: boolean;
	adjustForFooterNestedReplies?: boolean;
	topLevelParentCommentId?: string;
	commentHasChildren?: boolean;
	isLastNestedFooterReply?: boolean;
	isOffline?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	replyHasNestedObjects?: boolean;
};

const i18n = defineMessages({
	commentReopenedFlagTitle: {
		id: 'object-comments.comment.reopened.flag.title',
		defaultMessage: 'Comment reopened',
		description:
			'Title to display in Flag to inform user when the comment was successfully reopened.',
	},
	commentReopenFailedFlagTitle: {
		id: 'object-comments.comment.reopen.failed.flag.title',
		defaultMessage: 'Failed to reopen comment',
		description: 'Title to display in Flag to inform user when the comment failed to reopen.',
	},
	retryCTA: {
		id: 'object-comments.comment.reopen.failed.flag.cta.retry',
		defaultMessage: 'Retry now',
		description:
			'CTA button text displayed in Error Flag for users to retry the failed comment reopen action.',
	},
	cannotDeleteTitle: {
		id: 'object-comments.could.not.delete.title',
		defaultMessage: 'Unable to delete comment',
		description:
			'The title of the flag shown to a user that says their comment could not be deleted',
	},
});

const logger = getLogger('page-comments-queries');

// this component can be used for parent comment and a reply
export const ObjectComment = ({
	comment,
	supportedTopLevelActions,
	pageMode,
	pageInfo,
	isUnread = false,
	isHovered,
	threadKey,
	flags,
	adjustForNestedReplies = false,
	hideBranchingStyle = false,
	canAddComments = true,
	isThreadHovered,
	isParentCommentOpen,
	adjustForFooterNestedReplies = false,
	topLevelParentCommentId,
	commentHasChildren = false,
	isLastNestedFooterReply = false,
	isOffline = false,
	showRepliesWithCurvedBorders,
	replyHasNestedObjects = false,
}: CommentProps) => {
	// Store stable references to break circular dependency in useCallback
	const handleResolveCommentRef = useRef<(() => void) | null>(null);
	const handleReopenCommentRef = useRef<(() => void) | null>(null);
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { resetContentChanged } = useCommentsContentActions();
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [
		{
			commentsDataMap: legacyCommentsDataMap,
			hasNestedComments: legacyHasNestedComments,
			cacheUpdateFailed,
		},
		{ addNewCommentThreads, deleteParentComment, handleRemovingComments },
	] = useCommentsData();
	const { setCacheUpdateFailed } = useCommentsDataActions();

	// New comments data manager hooks - only used when experiment is enabled
	const newCommentsDataResult = useAllCommentsData({
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
		options: {
			fetchPolicy: 'cache-only',
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !contentId,
		},
	});
	const deleteCommentInCache = newCommentsDataResult.deleteCommentInCache;

	// Use new or legacy data based on experiment
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	// Use new or legacy hasNestedComments based on experiment
	const hasNestedComments = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? newCommentsDataResult.hasNestedComments
		: legacyHasNestedComments;

	const { cloudId } = useSessionData();

	const [
		{ currentlySelectedThreadKey, footerEditCommentIds },
		{
			setCurrentView,
			setCurrentlySelectedThreadKey,
			setReplyToCommentId,
			addFooterEditCommentId,
			removeFooterEditCommentId,
		},
	] = useCommentsPanel();

	const { formatMessage } = useIntl();
	const { focusedCommentId } = useInlineCommentQueryParams();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();

	const allowComments = useEmbeddedComments();

	const [resolveCommentFn] = useMutation<
		ObjectCommentResolveCommentMutation$data,
		ObjectCommentResolveCommentMutation$variables
	>(ObjectCommentResolveCommentMutation);
	const resolveCommentMutationFn = resolveCommentFn;

	const [reopenCommentFn] = useMutation<
		ObjectCommentReopenCommentMutation$data,
		ObjectCommentReopenCommentMutation$variables
	>(ObjectCommentReopenCommentMutation, {
		refetchQueries:
			// Copied from the existing dialog flow to support standalone experience
			pageMode === PageMode.VIEW
				? [{ query: InlineCommentsQuery, variables: { pageId: contentId } }]
				: [],
	});

	const [deleteGeneralCommentFn] = useMutation<
		ObjectCommentDeleteGeneralCommentMutation$data,
		ObjectCommentDeleteGeneralCommentMutation$variables
	>(ObjectCommentDeleteGeneralCommentMutation);
	// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
	const pageId: string = contentId;

	const userId = (comment?.author as InlineCommentAuthorKnownUser)?.accountId;
	const topCommentUserAvatar = comment?.author?.profilePicture?.path;
	const topCommentDisplayName =
		comment?.author?.displayName ?? formatMessage(commentsi18n.anonymous);
	const commentId = comment?.id;
	const date = comment?.version?.when;
	const createdAt = comment?.createdAt?.value;
	const createdAtNonLocalized = comment?.createdAtNonLocalized;
	const versionByDisplayName = comment?.version?.by?.displayName;
	const versionDate = comment?.version?.when;
	const permissions = comment?.permissions;
	const content = comment?.body?.value;
	const permissionType = comment?.author?.permissionType ?? undefined;
	const parentCommentId = comment?.parentId;
	const isReply = !!parentCommentId;
	const isEdited = (comment?.version?.number || 0) > 1;
	const numReplies = (comment as TopLevelComment)?.replies?.length || 0;
	const commentUrl = comment?.links.webui ?? undefined;
	const inheritedReactionsData = comment?.reactionsSummary;

	const footerCommentLocation = comment?.location as CommentFooterLocation;

	const getResolvedProperties = useCallback(() => {
		const properties = footerCommentLocation?.commentResolveProperties;
		if (!isReply) {
			return properties || undefined;
		}
		return undefined;
	}, [footerCommentLocation, isReply]);

	const resolveProperties = getResolvedProperties();

	const isCommentRemovedByAnotherUser = comment?.wasRemovedByAnotherUser;

	const isResolvedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD;
	const isDeletedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.DELETE_COMMENT;

	const deleteCommentInCacheOnUpdate = useCallback(
		(removalCommentId: string) =>
			deleteCommentInCache({
				commentId: removalCommentId,
				onError: (err) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName: DELETE_PAGE_COMMENT_EXPERIENCE,
						error: err,
						commentId: removalCommentId,
					});
				},
			}),
		[deleteCommentInCache, experienceTracker],
	);

	const nonMemoizedHandleDeleteGeneralComment = () => {
		let deleteLocation: CommentDeletionLocation | undefined;

		const deleteVariables = {
			variables: {
				commentIdToDelete: commentId,
				deleteFrom: deleteLocation,
			},
			update: selectUpdateFunction<ObjectCommentDeleteGeneralCommentMutation$data>(
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
				() => deleteCommentInCacheOnUpdate(commentId),
				() =>
					isReply
						? (updateApolloCacheGeneralReplyCallback(
								'delete',
								{
									pageId,
									contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
									type: [CommentsType.FOOTER],
								},
								parentCommentId,
								isReactionsEnabled,
								commentId,
							) as MutationUpdaterFn<ObjectCommentDeleteGeneralCommentMutation$data>)
						: (updateApolloCacheGeneralParentCallback(
								'delete',
								{
									pageId,
									contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
									type: [CommentsType.FOOTER],
								},
								isReactionsEnabled,
								commentId,
							) as MutationUpdaterFn<ObjectCommentDeleteGeneralCommentMutation$data>),
			),
		};

		deleteGeneralCommentFn({ ...deleteVariables })
			.then(({ data }) => {
				if (!data || !data.deleteComment) {
					throw new Error('No data returned from mutation');
				}

				resetContentChanged();

				if (isReply) {
					handleRemovingComments({
						threadKey,
						commentId,
						action: CommentActionType.DELETE_COMMENT,
						commentType: CommentType.GENERAL,
					});
				} else {
					deleteParentComment({
						threadKey,
						commentType: CommentType.GENERAL,
					});
				}

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'deleted',
						actionSubject: 'comment',
						actionSubjectId: commentId,
						objectType: 'page',
						objectId: pageId,
						source: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							commentType: AnalyticsCommentType.PAGE,
							source: AnalyticsSource.PAGE_FOOTER,
							pageMode,
							isReply,
						},
					},
				}).fire();

				experienceTracker.succeed({
					name: DELETE_PAGE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err) => {
				if (fg('confluence_frontend_show_already_deleted_flag')) {
					const parsedError = parseError(err);
					const translatedError = getTranslatedError(parsedError.message, commentId);

					if (isAlreadyDeletedError(translatedError)) {
						void flags?.showWarningFlag({
							title: <FormattedMessage {...i18n.cannotDeleteTitle} />,
							description: <FormattedMessage {...translatedError} />,
						});

						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'alreadyDeleted',
								actionSubject: 'comment',
								actionSubjectId: commentId,
								objectType: 'page',
								objectId: pageId,
								source: AnalyticsSource.PAGE_FOOTER,
								attributes: {
									commentType: CommentType.GENERAL,
									mode: pageMode,
									isReply,
								},
							},
						}).fire();
					}
				}

				void handleMutationFailure({
					experienceTracker,
					experienceName: DELETE_PAGE_COMMENT_EXPERIENCE,
					error: err,
					commentId,
				});
			});
	};

	const memoizedHandleDeleteGeneralComment = useCallback(nonMemoizedHandleDeleteGeneralComment, [
		commentId,
		createAnalyticsEvent,
		deleteGeneralCommentFn,
		deleteParentComment,
		deleteCommentInCacheOnUpdate,
		experienceTracker,
		flags,
		handleRemovingComments,
		isReactionsEnabled,
		isReply,
		pageId,
		pageMode,
		parentCommentId,
		resetContentChanged,
		threadKey,
	]);

	/**
	 * editComment, deleteComment, and resolveComment cause instability in CommentBody when rendering. Although
	 * useStableCallback would be more ideal to simplify the overhead for consumers of CommentBody, these functions
	 * as props are used as proxies for permissions meaning they could be undefined which violates the typing for useStableCallback
	 */
	const handleDeleteGeneralComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoizedHandleDeleteGeneralComment
		: nonMemoizedHandleDeleteGeneralComment;

	const nonMemoHandleEditComment = () => {
		addFooterEditCommentId(commentId);
	};

	const memoizedHandleEditComment = useCallback(nonMemoHandleEditComment, [
		addFooterEditCommentId,
		commentId,
	]);

	const handleEditComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoizedHandleEditComment
		: nonMemoHandleEditComment;

	const exitCommentEditor = () => {
		removeFooterEditCommentId(commentId);
	};

	const handleReplyToComment = (id: string) => {
		setReplyToCommentId({
			// If we allow nested comments, reply directly to the comment (set in CommentActions)
			// Otherwise, reply to the to the parent comment
			parentCommentId: hasNestedComments ? id : topLevelParentCommentId || id,
			location: LocationValue.FOOTER,
			user: {
				// This field controls who we auto mention in the reply
				displayName: comment?.author?.displayName,
				accountId: (comment?.author as InlineCommentAuthorKnownUser)?.accountId,
			},
		});
	};

	const nonMemoHandleReopenComment = () => {
		// NOTE: The reopen experience is started in CommentActions.tsx
		reopenCommentFn({
			variables: { commentId, cloudId },
		})
			.then(() => {
				// Update the state for panel experience
				const commentsDataMapClone = { ...commentsDataMap };
				const commentInMap = commentsDataMapClone[CommentType.GENERAL][threadKey];
				if (commentInMap) {
					commentInMap.isOpen = true;
					commentInMap.wasRemovedByAnotherUser = false;

					addNewCommentThreads(commentsDataMapClone);

					const commentLocation = commentInMap.location;

					if (
						commentLocation &&
						'commentResolveProperties' in commentLocation &&
						commentLocation.commentResolveProperties
					) {
						commentLocation.commentResolveProperties.resolved = false;
					}
				}

				// Show the reopen flag
				void flags?.showSuccessCircleFlag({
					id: commentId,
					title: formatMessage(i18n.commentReopenedFlagTitle),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(inlineCommentsI18n.goToCommentCTA),
							onClick: () => {
								if (!isCommentsPanelShown) {
									showCommentsPanel({ openWithView: ViewValues.OPEN });
								} else {
									setCurrentView(ViewValues.OPEN);
								}
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
						{
							content: formatMessage(inlineCommentsI18n.undoCTA),
							onClick: () => {
								handleResolveCommentRef.current?.();
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
					],
				});
			})
			.catch(() => {
				void flags?.showErrorFlag({
					id: commentId,
					title: formatMessage(i18n.commentReopenFailedFlagTitle),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(i18n.retryCTA),
							onClick: () => {
								void flags.hideFlag(flags.state.flags[0]?.id);
								handleReopenCommentRef.current?.();
							},
						},
					],
				});
			});
	};

	const memoHandleReopenComment = useCallback(nonMemoHandleReopenComment, [
		addNewCommentThreads,
		cloudId,
		commentId,
		commentsDataMap,
		flags,
		formatMessage,
		isCommentsPanelShown,
		reopenCommentFn,
		setCurrentView,
		showCommentsPanel,
		threadKey,
	]);

	const nonMemoHandleResolveComment = () => {
		const mutationVariables = { commentIds: [commentId], cloudId };
		const query = ActiveCommentsQuery;

		const queryVariables = buildConsistentActiveCommentsQueryVariables({
			pageId,
			type: CommentsType.FOOTER, // If we have footer, always write to footer. Panel works either way
		});

		// NOTE: The RESOLVED experience is started in CommentActions.tsx
		resolveCommentMutationFn({
			variables: mutationVariables,
			update: (cache) => {
				// readQuery can still return null which complicates TS lint errors with optional chaining
				// once we move to v3 of the apollo client we can move to cache.modify
				try {
					const dataProxy = cache.readQuery<ActiveCommentsQueryType>({
						query,
						variables: queryVariables,
					});

					const commentsToWrite = Object.assign({}, dataProxy?.comments);
					let totalCount = commentsToWrite.totalCount || 0;
					const commentsList = commentsToWrite.nodes?.filter(
						(n) => n !== null,
					) as TopLevelComment[];

					const idxToRemove = commentsList.findIndex((c) => c.id === commentId);

					// Remove the newly resolved comment from the list if we can find it
					if (idxToRemove !== -1) {
						commentsList.splice(idxToRemove, 1);
						totalCount--;
					}

					cache.writeQuery<ActiveCommentsQueryType>({
						query,
						variables: queryVariables,
						data: {
							comments: {
								nodes: commentsList,
								totalCount,
							},
						},
					});
					// cache updated successfuly
					// now check if previous cache update was failed
					if (cacheUpdateFailed) {
						setCacheUpdateFailed(false);
					}
				} catch (err) {
					logger.error`An error occurred when updating cache for comments panel resolve - ${err}`;
					setCacheUpdateFailed(true);
				}
			},
		})
			.then(() => {
				// Update the state for panel experience
				const commentsDataMapClone = { ...commentsDataMap };
				const commentInMap = commentsDataMapClone[CommentType.GENERAL][threadKey];

				if (commentInMap) {
					commentsDataMapClone[CommentType.GENERAL][threadKey].isOpen = false;
					const commentLocation = commentsDataMapClone[CommentType.GENERAL][threadKey]
						.location as GeneralCommentLocation;
					if (commentLocation && commentLocation.commentResolveProperties) {
						commentLocation.commentResolveProperties.resolved = true;
					}
					addNewCommentThreads(commentsDataMapClone);
				}

				handleResolveSuccess({
					commentId,
					parentCommentMarkerRef: threadKey,
					pageId,
					pageMode,
					source: AnalyticsSource.PAGE_FOOTER,
					commentType: CommentType.GENERAL,
					createAnalyticsEvent,
					//getInlineNodeTypes  // TODO: Figure out how to get this info
				});

				if (allowComments) {
					void flags?.showSuccessCircleFlag({
						id: commentId,
						title: formatMessage(inlineCommentsI18n.resolvedFlagText),
						isAutoDismiss: true,
						actions: [
							{
								content: formatMessage(inlineCommentsI18n.goToCommentCTA),
								onClick: () => {
									if (!isCommentsPanelShown) {
										showCommentsPanel({ openWithView: ViewValues.RESOLVED });
									} else {
										setCurrentView(ViewValues.RESOLVED);
									}

									scrollCommentsPanelToThread({ annotationId: threadKey });

									void flags.hideFlag(flags.state.flags[0]?.id);
								},
							},
							{
								content: formatMessage(inlineCommentsI18n.undoCTA),
								onClick: () => {
									if (!isCommentsPanelShown && !fg('confluence_comments_dont_open_cp_on_reopen')) {
										showCommentsPanel({ openWithView: ViewValues.OPEN });
									}

									handleReopenCommentRef.current?.();
									void flags.hideFlag(flags.state.flags[0]?.id);
								},
							},
						],
					});
				}

				experienceTracker.succeed({
					name: RESOLVE_PAGE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err) => {
				void handleMutationFailure({
					experienceTracker,
					experienceName: RESOLVE_PAGE_COMMENT_EXPERIENCE,
					error: err,
				});
			});
	};

	const memoizedHandleResolveComment = useCallback(nonMemoHandleResolveComment, [
		addNewCommentThreads,
		allowComments,
		cacheUpdateFailed,
		cloudId,
		commentId,
		commentsDataMap,
		createAnalyticsEvent,
		experienceTracker,
		flags,
		formatMessage,
		isCommentsPanelShown,
		pageId,
		pageMode,
		resolveCommentMutationFn,
		scrollCommentsPanelToThread,
		setCacheUpdateFailed,
		setCurrentView,
		showCommentsPanel,
		threadKey,
	]);

	const handleResolveComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoizedHandleResolveComment
		: nonMemoHandleResolveComment;

	const handleReopenComment = expValEquals(
		'cc_fe_performance_page_comments_stability',
		'cohort',
		'experiment',
	)
		? memoHandleReopenComment
		: nonMemoHandleReopenComment;

	// Update refs to break circular dependency
	handleResolveCommentRef.current = handleResolveComment;
	handleReopenCommentRef.current = handleReopenComment;

	const pageType = pageInfo?.type ?? '';

	// Editor setup
	const spaceId = pageInfo?.space?.id ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	// We only want to focus resolved comments because the 'active' comments have
	// their own treatment for when one is hovered/selected and could cause weird behavior
	const isCommentFocused = resolveProperties?.resolved && commentId === focusedCommentId;

	const supportedActions =
		!isReply && isCommentRemovedByAnotherUser
			? (['reopen'] as CommentAction[])
			: supportedTopLevelActions;

	return footerEditCommentIds?.has(commentId) ? (
		<EditComment
			pageId={pageId}
			date={date ?? ''}
			createdAtNonLocalized={createdAtNonLocalized}
			pageType={pageType}
			commentId={commentId}
			annotationId={threadKey}
			spaceId={spaceId}
			content={content ?? ''}
			isReply={isReply}
			displayCommentInViewMode={exitCommentEditor}
			avatarUrl={topCommentUserAvatar ?? ''}
			displayName={topCommentDisplayName}
			dateUrl={commentUrl ?? ''}
			versionByDisplayName={versionByDisplayName ?? ''}
			versionDate={versionDate ?? ''}
			mode={pageMode === PageMode.VIEW ? 'view' : 'edit'}
			hasMediaUploadPermissions={hasMediaUploadPermissions}
			isFooterComment
			commentType={CommentType.GENERAL}
			adjustForFooterReplies={commentHasChildren || !isReply}
			isLastNestedFooterReply={isLastNestedFooterReply}
			isOffline={isOffline}
		/>
	) : (
		<CommentBody
			mode="view"
			annotationId={threadKey}
			userId={userId}
			avatarUrl={topCommentUserAvatar}
			displayName={topCommentDisplayName}
			date={date ?? ''}
			dateUrl={commentUrl ?? ''}
			createdAt={createdAt ?? ''}
			createdAtNonLocalized={createdAtNonLocalized ?? ''}
			versionByDisplayName={versionByDisplayName ?? ''}
			versionDate={versionDate ?? ''}
			pageId={pageId}
			pageType={pageType}
			commentId={commentId ?? ''}
			isReply={isReply}
			isEdited={isEdited}
			parentCommentId={parentCommentId}
			permissions={permissions}
			content={content ?? ''}
			permissionType={permissionType ?? undefined}
			supportedActions={supportedActions}
			deleteComment={handleDeleteGeneralComment}
			editComment={handleEditComment}
			resolveComment={handleResolveComment}
			numReplies={numReplies}
			isUnread={isUnread}
			isCommentActive
			isCommentsPanel
			isHovered={isHovered}
			isResolvedByAnotherUser={isResolvedByAnotherUser}
			isFocused={isCommentFocused}
			resolveProperties={resolveProperties}
			replyToComment={handleReplyToComment}
			inheritedReactionsData={inheritedReactionsData}
			isDeletedByAnotherUser={isDeletedByAnotherUser}
			currentlySelectedThreadKey={currentlySelectedThreadKey}
			setCurrentlySelectedThreadKey={setCurrentlySelectedThreadKey}
			commentType={CommentType.GENERAL}
			adjustForNestedReplies={adjustForNestedReplies}
			hideBranchingStyle={hideBranchingStyle}
			canAddComments={canAddComments}
			isThreadHovered={isThreadHovered}
			isParentCommentOpen={isParentCommentOpen}
			isFooterComment
			adjustForFooterNestedReplies={adjustForFooterNestedReplies}
			isOffline={isOffline}
			showRepliesWithCurvedBorders={showRepliesWithCurvedBorders}
			replyHasNestedObjects={replyHasNestedObjects}
		/>
	);
};
